// NR-4 — the files in a Media or Long form review (Specs/Launch Reviews — New Review Builder — PRD
// (Nov 4, 2026).md). The author uploads new files or picks ones already in Launch Media. New uploads
// go through the normal Launch Media upload, tagged with the review's name. Each file becomes one
// card; the review only stores the file's id and name.
import { useEffect, useRef, useState } from "react";

import { getSessionToken } from "@/api/auth";
import type { MediaFile } from "@/Apps/Media/library";
import { finishUpload, listMedia, sendToStorage, startUpload, tagFile } from "@/api/media";
import type { ReviewItem } from "./api";

export type FileRow = { key: string; id: string; title: string; media: { id: string; name?: string } };
export type FileKind = "media" | "long";

const WANT = {
  media: { noun: "video", type: "video", accept: "video/*", fits: (f: File) => f.type.startsWith("video/") || /\.(mp4|mov|m4v|webm)$/i.test(f.name), fallback: "video/mp4" },
  long: { noun: "PDF", type: "pdf", accept: "application/pdf,.pdf", fits: (f: File) => f.type === "application/pdf" || /\.pdf$/i.test(f.name), fallback: "application/pdf" },
};

let seq = 0;
const row = (id: string, title: string, media: FileRow["media"]): FileRow => ({ key: `f${++seq}`, id, title, media });
const baseName = (name: string) => name.replace(/\.[^.]+$/, "");

export const rowsFromItems = (items: ReviewItem[]): FileRow[] =>
  items.filter((it) => it.media).map((it) => row(it.id, it.title || "", it.media!));

// a file keeps its id once it has one; new ones get the next free f-number
export const itemsFromRows = (rows: FileRow[], taken: Iterable<string> = []): ReviewItem[] => {
  const used = new Set([...taken, ...rows.map((r) => r.id).filter(Boolean)]);
  let n = 0;
  const next = () => { do n++; while (used.has(`f${n}`)); used.add(`f${n}`); return `f${n}`; };
  return rows.map((r) => ({ id: r.id || next(), title: r.title.trim() || r.media.name || "Untitled", media: r.media }) as ReviewItem);
};

type Props = { kind: FileKind; reviewName: string; rows: FileRow[]; onChange: (fn: (rows: FileRow[]) => FileRow[]) => void; onUploading: (busy: boolean) => void };

export function BuilderFiles({ kind, reviewName, rows, onChange, onUploading }: Props) {
  const want = WANT[kind];
  const [problem, setProblem] = useState("");
  const [progress, setProgress] = useState<{ name: string; pct: number } | null>(null);
  const [library, setLibrary] = useState<MediaFile[] | null>(null);
  const [picking, setPicking] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => () => onUploading(false), []);

  const upload = async (list: FileList | null) => {
    const files = Array.from(list || []);
    if (input.current) input.current.value = "";
    if (!files.length) return;
    setProblem("");
    onUploading(true);
    const token = getSessionToken() || "";
    for (const file of files) {
      if (!want.fits(file)) { setProblem(`“${file.name}” isn't a ${want.noun}. This review takes ${want.noun}s only.`); continue; }
      try {
        setProgress({ name: file.name, pct: 0 });
        const contentType = file.type || want.fallback;
        const { key, uploadUrl } = await startUpload(token, { name: file.name, contentType, size: file.size });
        await sendToStorage(uploadUrl, file, (pct) => setProgress({ name: file.name, pct }), contentType);
        const saved = await finishUpload(token, key, file.name);
        onChange((rs) => [...rs, row("", baseName(file.name), { id: saved.id, name: file.name })]);
        // the file is safe in Launch Media by now: a tag that fails doesn't lose it
        if (reviewName.trim()) await tagFile(token, saved.id, { add: reviewName.trim() }).catch(() => {});
      } catch (err) {
        setProblem(`“${file.name}” didn't upload. ${err instanceof Error ? err.message : "Try again."}`);
      }
    }
    setProgress(null);
    onUploading(false);
  };

  const openPicker = async () => {
    setPicking(true); setProblem("");
    try { setLibrary((await listMedia(getSessionToken() || "")).files.filter((f) => f.type === want.type)); }
    catch (err) { setPicking(false); setProblem(err instanceof Error ? err.message : "Couldn't open Launch Media. Try again."); }
  };
  const pick = (f: MediaFile) => onChange((rs) => [...rs, row("", baseName(f.name), { id: f.id, name: f.name })]);
  const inReview = new Set(rows.map((r) => r.media.id));

  const edit = (i: number, title: string) => onChange((rs) => rs.map((r, j) => (j === i ? { ...r, title } : r)));
  const move = (i: number, by: number) => onChange((rs) => { const to = i + by; if (to < 0 || to >= rs.length) return rs; const ys = [...rs]; [ys[i], ys[to]] = [ys[to], ys[i]]; return ys; });

  return (
    <section className="nr-sec" aria-label="Files">
      <h2>{kind === "media" ? "Videos" : "Documents"} <small>{rows.length}</small></h2>
      {!rows.length && <p className="nr-hint">Add the {want.noun}s people should review. Each one becomes its own card. Only people on this review can open them here.</p>}
      <ol className="nr-qs">
        {rows.map((r, i) => (
          <li key={r.key} className="nr-q">
            <div className="nr-qtop">
              <span className="nr-n">{i + 1}</span>
              <input aria-label={`Title for file ${i + 1}`} value={r.title} placeholder="What people should call this" onChange={(e) => edit(i, e.target.value)} />
              <span className="nr-qtools">
                <button type="button" aria-label={`Move file ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                <button type="button" aria-label={`Move file ${i + 1} down`} disabled={i === rows.length - 1} onClick={() => move(i, 1)}>↓</button>
                <button type="button" aria-label={`Remove file ${i + 1}`} className="danger" onClick={() => onChange((rs) => rs.filter((_, j) => j !== i))}>✕</button>
              </span>
            </div>
            <div className="nr-meta"><span>{r.media.name}</span></div>
          </li>
        ))}
      </ol>
      {progress && <p className="nr-hint" role="status">Uploading {progress.name}… {progress.pct}%</p>}
      {problem && <p className="nr-problem" role="alert">{problem}</p>}
      <div className="nr-row">
        <button type="button" className="nr-add" disabled={!!progress} onClick={() => input.current?.click()}>Upload {want.noun}s</button>
        <input ref={input} type="file" hidden multiple accept={want.accept} aria-label={`Upload ${want.noun}s`} onChange={(e) => upload(e.target.files)} />
        {!picking && <button type="button" className="nr-ghost" onClick={openPicker}>Pick from Launch Media</button>}
      </div>
      {picking && (
        <div className="nr-paste" aria-label="Your Launch Media">
          {library === null ? <p className="nr-hint">Opening Launch Media…</p> : !library.length ? <p className="nr-hint">You don't have any {want.noun}s in Launch Media yet.</p> : (
            <ul className="nr-pick">
              {library.map((f) => (
                <li key={f.id}>
                  <span>{f.name}</span>
                  <button type="button" className="nr-ghost" disabled={inReview.has(f.id)} aria-label={`Add ${f.name}`} onClick={() => pick(f)}>{inReview.has(f.id) ? "Added" : "Add"}</button>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="nr-ghost" onClick={() => setPicking(false)}>Done</button>
        </div>
      )}
    </section>
  );
}
